/** React APP Context */
import React, { createContext, useEffect, useReducer } from "react";
import { useSearchParams } from "react-router-dom";

import {
	APPContext,
	AgentColorPallete,
	AppAction,
	AppState,
	CardId,
	ReportConfiguration,
	ReportInfo,
} from "../interfaces/AppDefinitions";
import { OwnerConsoleViewController } from "../controllers/OwnerConsoleViewController";
import { createTheme, ThemeOptions, ThemeProvider } from "@mui/material";
import { green } from "@mui/material/colors";
export const APP_COLOR_SCHEME = green;
export const AppContext = createContext<APPContext>({} as APPContext);
const initialColorPallete: AgentColorPallete = {
	scheme1: "#16B380",
	scheme2: "#EDFCF5",
	scheme3: "#16B380",
	scheme4: "#e1dee9",
	scheme5: "#16B380",
};
const lightOptions: ThemeOptions = {
	typography: {
		fontFamily: "Plus Jakarta Sans, sans-serif",
		fontSize: 14,
		fontWeightRegular: 400,
	},
	palette: {
		mode: "light",
		primary: {
			main: initialColorPallete["scheme1"],
		},
		secondary: {
			main: initialColorPallete["scheme2"],
		},
	},
	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					"&.Mui-disabled": {
						pointerEvents: "unset", // allow :hover styles to be triggered
						cursor: "not-allowed", // and custom cursor can be defined without :hover state
					},
				},
			},
		},
		MuiTextField: {
			styleOverrides: {
				root: {
					// backgroundColor: APP_COLOR_SCHEME[200],
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					border: "0.5px solid var(--stroke-soft-200, #E1E4EA)",
					boxShadow: "0px 0px 0px rgba(0, 0, 0, 0.05)",
					borderRadius: "10px",
				},
			},
		},
	},
	spacing: 12,
};
const darkOptions: ThemeOptions = {
	palette: {
		mode: "dark",
	},
};
export function AppProvider(props: any) {
	const [queryParameters] = useSearchParams();
	const initialState: AppState = {
		appInitialized: false,
		reportMenu: [],
		report_configuration: new Array<ReportConfiguration>(),
		info: { agent: {}, client: {} } as ReportInfo,
		owner_adjustment_types: [],
		footerDisclaimer: footerDisclaimer,
		disclaimer: disclaimer,
		trackView: false,
		showErrorPage: false,
		theme: createTheme(lightOptions),
		drawerOpen: false,
		isDarkMode: false,
		currentAddrId: undefined,
		isFetchingAdjustments: false,
		selectedCard: CardId.CURRENTVALUE,
		colorPallete: initialColorPallete,
	};
	const [appState, appDispatch] = useReducer(reducer, initialState);

	useEffect(() => {
		var c = queryParameters.get("enc");
		var notrack = queryParameters.get("no_track");
		let trackView = true;
		if (notrack) {
			trackView = false;
		}
		init(trackView);

		async function init(trackView = false) {
			try {
				if (!c) return;
				const result = await OwnerConsoleViewController.GetReportConfiguration(c, trackView);
				if (result && result.report_configuration) {
					const { last_updated, report_configuration, info, owner_adjustment_types } = result;
					appDispatch({
						type: "INIT",
						payload: {
							colorPallete: result.info?.agent.reportColorPallete,
							theme: createTheme({
								...lightOptions,
								palette: {
									mode: "light",
									primary: {
										main: result.info?.agent.reportColorPallete["scheme1"],
									},
									secondary: {
										main: result.info?.agent.reportColorPallete["scheme2"],
									},
								},
							}),
							last_updated: last_updated,
							report_configuration: report_configuration as Array<ReportConfiguration>,
							info: info as ReportInfo,
							enc: c,
							owner_adjustment_types: [
								{
									adjustment_type_id: -1,
									adjustment_type: "Please select an adjustment",
								},
								...owner_adjustment_types,
							],
							appInitialized: true,
							trackView: trackView,
						},
					});
					if (report_configuration.length > 0) {
						appDispatch({
							type: "SET_CURRENT_ADDR_ID",
							value: report_configuration[0].addrId,
						});
					}
				} else {
					throw new Error("No report configuration found");
				}
			} catch (err) {
				console.log(err);
				appDispatch({
					type: "INIT",
					payload: { showErrorPage: true, appInitialized: true },
				});
			}
		}
	}, [queryParameters]);

	return (
		<AppContext.Provider value={{ appState, appDispatch }}>
			<ThemeProvider theme={appState.theme}>{props.children}</ThemeProvider>
		</AppContext.Provider>
	);
}

function reducer(state: AppState, action: AppAction) {
	switch (action.type) {
		case "INIT":
			return {
				...state,
				...action.payload,
			};
		case "UPDATE_STATE":
			return {
				...state,
				[action.key]: action.value,
			};
		case "SET_CURRENT_ADDR_ID":
			return {
				...state,
				currentAddrId: action.value,
			};

		case "TOGGLE_DRAWER":
			return {
				...state,
				drawerOpen: !state.drawerOpen,
			};
		case "TOGGLE_DARK_MODE":
			return {
				...state,
				theme: createTheme(state.isDarkMode ? lightOptions : darkOptions),
				isDarkMode: !state.isDarkMode,
			};
		case "SET_FETCHING_ADJUSTMENTS":
			return {
				...state,
				isFetchingAdjustments: action.value,
			};
		case "SET_SELECTED_CARD":
			return {
				...state,
				selectedCard: action.id,
			};
		default: {
			throw Error("Unknown action to app appDispatch: ");
		}
	}
}

const footerDisclaimer = `The information contained in this report has been compiled by
  GetKeys Realty Inc. from sources believed to be reliable, but no
  representation or warranty, express or implied, is made by
  GetKeys Realty Inc., or any other person as to its accuracy,
  completeness or correctness. All opinions and estimates
  contained in this report constitute GetKeys Realty Inc. judgment
  as of the date of this report, are subject to change without
  notice and are provided in good faith but without legal
  responsibility. Nothing in this report constitutes legal,
  accounting or tax advice or individually tailored investment
  advice. This material is prepared for general circulation to
  clients and has been prepared without regard to the individual
  financial circumstances and objectives of persons who receive
  it. The analysis contained in this report may not be suitable
  for you and it is recommended that you consult an independent
  advisor if you are in doubt about the suitability of such
  investments or services. This report is not an offer to sell or
  a solicitation of an offer to buy any real estate property. Past
  performance is not a guide to future performance, future returns
  are not guaranteed, and a loss of original capital may occur.`;
const disclaimer = `The information in this report is not a suggested listing
  price or expected sold price for your home. Please contact me
  for a more detailed market evaluation​.`;
/** React APP Context */
